import { useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

import {
    TABLE_CONSTANTS_COLUMS,
    useTableFilter,
    useTableServerSideFiltering,
} from "@/shared";
import { useQuery } from "@tanstack/react-query";
import { getAgendamiento, getOrdenTrabajo, useFetchAgendamientos } from "@/shared/actions/erp";
import { GeneralTable } from "@/shared/components/ui";
import { Agendamiento, OrdenTrabajo } from "@/shared/interfaces";
import { useTableColumnsOrdenServicioVisita } from "../../hooks/useTableColumnsOrdenServicioVisita";
import VisitaFinalizadaDetalle from "./VisitaFinalizadaDetalle";
import KpisCalidadVisitas from "./KpisCalidadVisitas";

/**
 * Panel para una visita que TODAVIA no tiene Orden de Trabajo (recien creada /
 * aun no agendada o sin tecnico asignado). Muestra los datos del agendamiento
 * para que el supervisor la vea desde su creacion, aunque no haya dossier de
 * calidad todavia.
 */
function VisitaNoIniciada({ ag }: { ag: Agendamiento }) {
    const cliente =
        (ag as any)?.prospecto_data?.razon_social ||
        (ag as any)?.contrato_data?.cliente_data?.razon_social ||
        "—";
    const Campo = ({ label, value }: { label: string; value?: any }) => (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {value || value === 0 ? value : "—"}
            </Typography>
        </Grid>
    );
    return (
        <Box sx={{ pt: 1 }}>
            <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "rgba(21,101,192,0.03)" }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <PendingActionsIcon fontSize="small" sx={{ color: "#ed6c02" }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                        Visita aún no iniciada por el técnico
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    La visita ya está registrada pero todavía no tiene Orden de Trabajo
                    (se genera al agendar/asignar el técnico). El dossier de calidad
                    (tiempos, fotos, activación) se llenará a medida que avance el flujo.
                </Typography>
            </Paper>
            <Grid container spacing={1.5}>
                <Campo label="Cliente" value={cliente} />
                <Campo label="Identificación" value={(ag as any)?.prospecto_data?.identificacion} />
                <Campo
                    label="Estado del agendamiento"
                    value={
                        <Chip
                            size="small"
                            variant="outlined"
                            label={ag?.estado_agendamiento || "—"}
                        />
                    }
                />
                <Campo label="Fecha de creación" value={(ag as any)?.fecha_creacion} />
                <Campo label="Dirección" value={ag?.direccion} />
                <Campo label="Coordenadas" value={ag?.coordenadas} />
                <Campo label="Flota" value={(ag as any)?.flota_data?.nombre} />
                <Campo label="Celular" value={ag?.celular} />
            </Grid>
        </Box>
    );
}

/**
 * Tab "Calidad de Visitas": lista TODAS las visitas desde su creación
 * (agendamientos de tipo VISITA, cualquier estado). La Orden de Trabajo no
 * existe hasta que la visita se agenda/asigna técnico, por eso se lista por
 * agendamiento (no por OT). Al abrir el detalle se resuelve la OT (si ya
 * existe) para el dossier de calidad; si no, se muestra el aviso de no iniciada.
 */
export default function VisitasFinalizadasTab() {
    const { pagination, setPagination } = useTableFilter();
    const { pageIndex, pageSize } = pagination;

    const { filterObject, columnFilters, setColumnFilters } =
        useTableServerSideFiltering();

    const { columns } = useTableColumnsOrdenServicioVisita();

    const [selected, setSelected] = useState<Agendamiento | null>(null);

    const { data, isLoading, isRefetching } = useFetchAgendamientos({
        enabled: true,
        params: {
            page: pageIndex + 1,
            page_size: pageSize,
            ...filterObject,
            filterByState: false,
            tipo_orden_servicio: "VISITA",
        } as any,
    });

    const rows: Agendamiento[] = data?.data?.data || [];

    // Si la visita seleccionada ya tiene OT, se trae completa para el dossier.
    // Refresca el agendamiento seleccionado mientras el modal está abierto
    // (~5s) para reflejar en (casi) tiempo real cuando el técnico inicia la
    // visita: se crea la OT y `id_orden_trabajo` pasa de null al id real. La
    // lista puede estar cacheada, por eso NO nos fiamos del id de la fila.
    const agId = selected?.id_agendamiento || "";
    const { data: freshAgRes } = useQuery({
        queryKey: ["calidad-visita-detalle", agId],
        queryFn: () => getAgendamiento(agId),
        enabled: !!agId,
        refetchInterval: agId ? 5000 : false,
        refetchOnWindowFocus: true,
    });
    const agDetalle = ((freshAgRes as any)?.data as Agendamiento) || selected;

    const otId =
        (agDetalle as any)?.id_orden_trabajo_actual ||
        (agDetalle as any)?.id_orden_trabajo ||
        "";
    // Poll de la OT mientras el modal está abierto para que el dossier
    // (tiempos, fotos, activación) se auto-actualice en vivo a medida que el
    // técnico avanza la visita, sin recargar.
    const { data: fullOtRes } = useQuery({
        queryKey: ["calidad-visita-ot", otId],
        queryFn: () => getOrdenTrabajo(otId),
        enabled: !!otId,
        refetchInterval: otId ? 5000 : false,
        refetchOnWindowFocus: true,
    });
    const fullOt = ((fullOtRes as any)?.data as OrdenTrabajo) ?? null;

    return (
        <Box sx={{ width: "100%" }}>
            <KpisCalidadVisitas rows={rows} />
            <GeneralTable<Agendamiento>
                columns={columns}
                data={rows}
                isLoading={isLoading}
                isRefetching={isRefetching}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                enableManualFiltering={true}
                enableGlobalFilter={false}
                pagination={pagination}
                onPaging={setPagination}
                rowCount={data?.data?.count}
                enableColumnActions={true}
                enableEditing={true}
                canEdit={false}
                showCustomActionBtnSpace={true}
                actionsColumnSize={TABLE_CONSTANTS_COLUMS.COLUMN_WIDTH_SMALL}
                customActionBtnSpace={(row: Agendamiento) => (
                    <Tooltip title="Ver calidad de la visita">
                        <IconButton onClick={() => setSelected(row)}>
                            <VisibilityIcon color="info" />
                        </IconButton>
                    </Tooltip>
                )}
            />

            <Dialog
                open={!!selected}
                onClose={() => setSelected(null)}
                fullScreen
                PaperProps={{ sx: { borderRadius: 0 } }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Stack spacing={0.25}>
                        <Typography variant="h6" fontWeight={700}>
                            Calidad de Visita — Supervisión
                        </Typography>
                        {selected && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    {(selected as any)?.prospecto_data?.razon_social ||
                                        (selected as any)?.contrato_data?.cliente_data
                                            ?.razon_social ||
                                        selected?.numero_referencia}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={agDetalle?.estado_agendamiento || "—"}
                                    color="info"
                                    variant="outlined"
                                />
                            </Stack>
                        )}
                    </Stack>
                    <IconButton
                        onClick={() => setSelected(null)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1, maxWidth: 1150, mx: "auto" }}>
                        {selected &&
                            (!otId ? (
                                <VisitaNoIniciada ag={agDetalle} />
                            ) : fullOt ? (
                                <VisitaFinalizadaDetalle ot={fullOt} />
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1.5,
                                        py: 6,
                                    }}
                                >
                                    <CircularProgress size={22} />
                                    <Typography variant="body2" color="text.secondary">
                                        Cargando dossier de la visita…
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
